import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, Redirect } from 'react-router-dom';

import { pathTo } from './routes';

import TeamSelection from '../components/TeamSelection';
import TeamView from '../components/TeamView';
import PlayerSelection from '../components/PlayerSelection';
import PlayerView from '../components/PlayerView';
import GamesSummary from '../components/GamesSummary';
import GameExplorer from '../components/GameExplorer';
import LineupsSummary from '../components/LineupsSummary';
import CombinationExplorer from '../components/CombinationExplorer';
import CombinationsSummary from '../components/CombinationsSummary';
import LineupsShow from '../components/LineupExplorer';
import Login from '../components/Login';
import ForgotPassword from '../components/ForgotPassword';
import ResetPassword from '../components/ResetPassword';
import AdvancedStats from '../components/AdvancedStats';
import OnOffMetricsShow from '../components/OnOffMetrics';
import Settings from '../components/Settings';
import StatsTable from '../components/StatsTable';

// Given a path, returns a function that renders a redirect
const redirectTo = path => () => <Redirect to={path} />;

const RouteToLogin = ({ path, exact }) => (
  <Route path={path} exact={exact} render={redirectTo('/login')} />
);
const RouteToRoot = ({ path, exact }) => (
  <Route path={path} exact={exact} render={redirectTo('/')} />
);

const Router = ({ isAuthenticated }) => {
  const AnonymousRoute = isAuthenticated ? RouteToRoot : Route;
  const AuthorizedRoute = isAuthenticated ? Route : RouteToLogin;

  return (
    <Switch>
      <AnonymousRoute path="/login" exact render={() => <Login />} />

      <AnonymousRoute
        path="/forgot-password"
        exact
        render={() => <ForgotPassword />}
      />

      <AnonymousRoute
        path="/reset-password/:token"
        exact
        render={({ match }) => <ResetPassword token={match.params.token} />}
      />

      <AuthorizedRoute
        path="/settings"
        exact
        render={() => <Settings section={'profile'} />}
      />
      <AuthorizedRoute
        path="/settings-password"
        exact
        render={() => <Settings section={'password'} />}
      />
      <AuthorizedRoute path="/teams" exact render={() => <TeamSelection />} />
      <AuthorizedRoute
        path="/players"
        exact
        render={() => <PlayerSelection />}
      />
      <AuthorizedRoute path="/stats" exact render={() => <StatsTable />} />
      <AuthorizedRoute
        path={pathTo('player', ':playerId')}
        exact
        render={({ match }) => <PlayerView playerId={+match.params.playerId} />}
      />
      <AuthorizedRoute
        path={pathTo('team', ':leagueId', ':seasonId', ':teamId')}
        exact
        render={({ match }) => (
          <TeamView
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
          />
        )}
      />
      <AuthorizedRoute
        path={pathTo('games', ':leagueId', ':seasonId', ':teamId')}
        exact
        render={({ match }) => (
          <GamesSummary
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
          />
        )}
      />
      <AuthorizedRoute
        path={pathTo(
          'game-explorer',
          ':leagueId',
          ':seasonId',
          ':teamId',
          ':gameId',
        )}
        exact
        render={({ match }) => (
          <GameExplorer
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
            gameId={+match.params.gameId}
          />
        )}
      />
      <AuthorizedRoute
        path={pathTo('lineups', ':leagueId', ':seasonId', ':teamId')}
        exact
        render={({ match }) => (
          <LineupsSummary
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
          />
        )}
      />
      <AuthorizedRoute
        path={pathTo(
          'lineup-explorer',
          ':leagueId',
          ':seasonId',
          ':teamId',
          ':lineupId?',
        )}
        exact
        render={({ match }) => (
          <LineupsShow
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
            lineupId={+match.params.lineupId}
          />
        )}
      />
      <AuthorizedRoute
        path={pathTo('onoff', ':leagueId', ':seasonId', ':teamId')}
        exact
        render={({ match }) => (
          <OnOffMetricsShow
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
          />
        )}
      />
      <AuthorizedRoute
        path={pathTo('stats', ':leagueId', ':seasonId', ':teamId')}
        exact
        render={({ match }) => (
          <AdvancedStats
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
          />
        )}
      />
      <AuthorizedRoute
        path={pathTo('combinations', ':leagueId', ':seasonId', ':teamId')}
        exact
        render={({ match }) => (
          <CombinationsSummary
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
          />
        )}
      />
      <AuthorizedRoute
        path={pathTo(
          'combination-explorer',
          ':leagueId',
          ':seasonId',
          ':teamId',
          ':combinationId?',
        )}
        exact
        render={({ match }) => (
          <CombinationExplorer
            leagueId={+match.params.leagueId}
            seasonId={+match.params.seasonId}
            teamId={+match.params.teamId}
            combinationId={+match.params.combinationId}
          />
        )}
      />
      <AuthorizedRoute path="/" render={redirectTo('/teams')} />
    </Switch>
  );
};
Router.propTypes = {
  isAuthenticated: PropTypes.bool,
};

export default Router;
